/**
 * システム管理トップ (`/admin/hub`) のカテゴリとショートカット。
 *
 * ## 分類は spec / design が持つ。サイドメニューの section とは別物
 *
 * `admin-hub.md` AC-AHB-001 が分類そのものを定めており (データ / 顧客 / 料金・燃調 /
 * マスタ / 業務ルール / 運用 / システム連携)、 ビジネスルールに
 * 「カード一覧の分類・並び順・見せ方はデザインに従う」 とある。 8080 プロト
 * `admin/index.html` の各カードも同じ 7 分類を `cat` で持つ。
 *
 * 初版はサイドメニューの `{ kind: 'section' }` から導出していたが、**サイドメニューは
 * 5 section で、 spec の 7 分類とは粒度も割り当ても違う**。 導出のままでは
 * AC-AHB-001 を満たせないので、 分類は spec の並びで明示的に持つ。
 *
 * 画面名は `routePaths` の `name` を引くので、 **文言の出所は増えない** (カード名と
 * メニュー名は常に同じ)。 カテゴリ見出しだけがハブ固有で、 spec の画面文言表に従う
 * (`nav.section.*` とは訳が違う: Ops → Operations / Master → Master data /
 * Customer → Customers。 spec が「略語のため綴りを戻した」 と明記している)。
 *
 * ## 区分の割り当ては 8080 プロトの `cat` と spec の決定に合わせる
 *
 * - **気象予測地点**: **データ** (プロト `cat:'データ'`)。
 *   需要想定に使う入力データの選択で、 事業者・設備のマスタとは性質が違う。
 * - **小売料金プラン**: **料金・燃調** (プロト `cat:'料金・燃調'`)。
 *   spec が料金を独立カテゴリに立てているため。
 * - **CSV入出力**: **マスタ** (プロト `cat:'マスタ'`)。
 *   2026-08-17 PdM 判断 (#5476) で配置が「システム管理 > CSV アップロード」 から
 *   **「システム管理 > マスタ > CSV入出力」** へ差し替わっている
 *   (`admin/data/uploads/index.md` の Discussion 表が SoT。 #3502 の旧決定を差し替え)。
 *   データ管理が扱う種別の大半が各マスタタブの一括入出力窓口であることが理由で、
 *   「1 件ずつ直す = 各マスタ」「まとめて入れ替える = CSV入出力」 の対で読ませる。
 *   初版は旧決定 (#3502) 時代のプロト `cat:'データ'` を写していたため データ に置いていた。
 *
 * ## 「業務ルール」 は 7 分類に含めるが、 中身が無いので表示されない
 *
 * **2026-10-01 PdM 判断 (#6158 Q4)**: 区分は廃止しない。 中身の画面がまだ無いので
 * 今は出さないだけ。 `admin-hub.md` AC-AHB-001 に「7 区分の並びはそのまま残し、
 * **表示できる画面が 1 つも無い区分は表示しない**」 を明記した。
 *
 * よって 7 分類すべてをこの配列に宣言し、 業務ルール は `shortcuts: []` で持つ。
 * **描画側 (`index.tsx`) が空の区分を落とす**ので、 画面には 6 区分が出る。
 * 区分をここから省くのではなく空で持つのは、 中身の画面ができた時に
 * **shortcut を 1 行足すだけで出るようにする**ため (省いていると区分の追加を忘れる)。
 *
 * 業務ルール に入る画面は **FIT特例制度 設定**
 * (`docs/specs/admin/business-rules/fit-special-settings.md`) で、 PdM によると
 * リリース範囲では P2-1 のため未実装。
 * **FIPプレミアムは入れない** — 供給計画の画面であり業務ルールの中身ではない
 * (#6158 で案 (a) は不採用)。 実装側の `adminFipPremium` もサイドメニューの
 * 供給計画 グループ (`prefix: '/supply'`) に属している。
 */
import {
  FaBellRegular,
  FaBoltRegular,
  FaBuildingColumnsRegular,
  FaChartColumnRegular,
  FaChartLineRegular,
  FaClipboardRegular,
  FaClockRegular,
  FaClockRotateLeftRegular,
  FaCloudSunRegular,
  FaDatabaseRegular,
  FaFileInvoiceRegular,
  FaFileSpreadsheetRegular,
  FaLinkSimpleRegular,
  FaSitemapRegular,
  FaSlidersRegular,
  FaSunBrightRegular,
  FaUserGroupRegular,
  FaUserRegular,
} from '@enechain/ecloud-icons'

import { routePaths } from '~/routes/routePaths'

export type AdminHubIcon = typeof FaSlidersRegular

export type AdminHubShortcut = {
  path: string
  nameKey: string
  /**
   * カードの説明文 (その画面で何を扱うか / 中にどのタブがあるか) の i18n key。
   * design `admin/index.html` の `.hub-card-desc` に当たる。
   */
  descriptionKey: string
  /** カードの icon。 画面ごとに中身が分かる絵を選ぶ (8080 の `.hub-card-icon`)。 */
  icon: AdminHubIcon
}

export type AdminHubCategory = {
  /** カテゴリ見出しの i18n key (ハブ固有。 spec の画面文言表に従う)。 */
  titleKey: string
  shortcuts: readonly AdminHubShortcut[]
}

/** `routePaths` の 1 件を カード 1 枚に写す (画面名は routePaths の name を引く)。 */
function shortcut(
  route: { path: string; name: string },
  descriptionKey: string,
  icon: AdminHubIcon,
): AdminHubShortcut {
  return { path: route.path, nameKey: route.name, descriptionKey, icon }
}

/**
 * spec AC-AHB-001 の 7 分類すべてを、 **利用頻度の高い順**に並べる (2026-10-05 デザイナー判断)。
 *
 * spec は分類だけを決め、 並び順は「デザインに従う」 (admin-hub.md §1 ビジネスルール)。
 * 初版は AC の列挙順 (データ / 顧客 / 料金・燃調 / マスタ / 業務ルール / 運用 / システム連携) の
 * ままで、 毎日開く 運用 (承認案件一覧) が 6 番目、 年に数回の 料金・燃調 が 3 番目に来ていた。
 * 頻度の目安は 8080 プロトが持っていた分類 (旧「運用頻度順」 表示: 高い = 運用・顧客・マスタ /
 * 低い = データ・システム連携・料金・燃調・業務ルール) を土台に、 区分の中身で並べ替えた。
 * 各区分の頻度の根拠は配列の各要素のコメント。
 *
 * 中身が空の区分 (= 現時点では 業務ルール) は**この配列には残し、 描画側で落とす**
 * (AC-AHB-001 の「表示できる画面が 1 つも無い区分は表示しない」)。
 * 表示用の配列は [[visibleAdminHubCategories]]。
 */
export const adminHubCategories: readonly AdminHubCategory[] = [
  {
    // 承認案件一覧は承認者が毎日開く。 日々の業務から最も近い
    titleKey: 'page:adminHub.category.operations',
    shortcuts: [
      shortcut(
        routePaths.adminApprovalTasks,
        'page:adminHub.cardDescription.approvalTasks',
        FaClipboardRegular,
      ),
      shortcut(
        routePaths.adminPlanAutoSubmit,
        'page:adminHub.cardDescription.planAutoSubmit',
        FaClockRegular,
      ),
      shortcut(
        routePaths.adminUsers,
        'page:adminHub.cardDescription.users',
        FaUserRegular,
      ),
    ],
  },
  {
    // CSV入出力 (日次〜週次の入稿) と、 事業者・BG・取引先の変更で開く広域マスタ
    titleKey: 'page:adminHub.category.master',
    shortcuts: [
      shortcut(
        routePaths.adminOccto,
        'page:adminHub.cardDescription.occto',
        FaSitemapRegular,
      ),
      shortcut(
        routePaths.adminPlantUnitMaster,
        'page:adminHub.cardDescription.plantUnitMaster',
        FaBoltRegular,
      ),
      shortcut(
        routePaths.adminEquipment,
        'page:adminHub.cardDescription.equipment',
        FaSunBrightRegular,
      ),
      shortcut(
        routePaths.adminLossRate,
        'page:adminHub.cardDescription.lossRate',
        FaChartColumnRegular,
      ),
      shortcut(
        routePaths.adminDemandCurve,
        'page:adminHub.cardDescription.demandCurve',
        FaChartLineRegular,
      ),
      // CSV入出力 は各マスタの「まとめて入れ替える」 窓口なので、 個別マスタの後に置く
      // (#5476。 上の doc の「区分の割り当て」 参照)
      shortcut(
        routePaths.adminUploads,
        'page:adminHub.cardDescription.uploads',
        FaFileSpreadsheetRegular,
      ),
    ],
  },
  {
    // 需要家の増減に合わせて組み替える (月次程度)
    titleKey: 'page:adminHub.category.customer',
    shortcuts: [
      shortcut(
        routePaths.adminDemandGroups,
        'page:adminHub.cardDescription.demandGroups',
        FaUserGroupRegular,
      ),
    ],
  },
  {
    // 連携エラー・証明書の期限切れなど、 異常時に確かめに来る
    titleKey: 'page:adminHub.category.integration',
    shortcuts: [
      shortcut(
        routePaths.adminLinkage,
        'page:adminHub.cardDescription.linkage',
        FaLinkSimpleRegular,
      ),
      shortcut(
        routePaths.adminJepxMemberInfo,
        'page:adminHub.cardDescription.jepxMemberInfo',
        FaBuildingColumnsRegular,
      ),
      shortcut(
        routePaths.adminCommLog,
        'page:adminHub.cardDescription.commLog',
        FaClockRotateLeftRegular,
      ),
      shortcut(
        routePaths.adminNotificationSettings,
        'page:adminHub.cardDescription.notificationSettings',
        FaBellRegular,
      ),
    ],
  },
  {
    // 気象予測地点は導入時に決めて以後ほぼ変えない。 データベースは突合・調査の時にだけ開く
    titleKey: 'page:adminHub.category.data',
    shortcuts: [
      shortcut(
        routePaths.adminDatabase,
        'page:adminHub.cardDescription.database',
        FaDatabaseRegular,
      ),
      shortcut(
        routePaths.adminWeatherForecastPoints,
        'page:adminHub.cardDescription.weatherForecastPoints',
        FaCloudSunRegular,
      ),
    ],
  },
  {
    // 料金改定時だけ (年数回)
    titleKey: 'page:adminHub.category.rate',
    shortcuts: [
      shortcut(
        routePaths.adminRatePlan,
        'page:adminHub.cardDescription.ratePlan',
        FaFileInvoiceRegular,
      ),
    ],
  },
  {
    // 中身は FIT特例制度 設定 (`admin/business-rules/fit-special-settings.md`、 P2-1) のみで
    // 未実装なので、 現時点では空。 空の区分は描画側で落とす (AC-AHB-001 / #6158)。
    // 当該画面を実装したら、 ここに shortcut を 1 行足せば区分ごと出る。
    titleKey: 'page:adminHub.category.businessRules',
    shortcuts: [],
  },
]

/**
 * 実際に画面へ出す区分。
 *
 * `admin-hub.md` AC-AHB-001: **表示できる画面が 1 つも無い区分は表示しない**
 * (2026-10-01 PdM 判断 #6158)。 空の区分を見出しだけ出すと「未実装」 ではなく
 * 「設定が足りない」 と誤読されるため。
 *
 * 松竹梅プランによるカードの出し分け (#9228 の「プランで使えない画面はカードから外す」)
 * が入ったら、 ここをプラン適用後の shortcuts で絞ってから同じ判定を掛ける
 * (= 絞った結果 0 件になった区分も落ちる)。
 */
export const visibleAdminHubCategories: readonly AdminHubCategory[] =
  adminHubCategories.filter((category) => category.shortcuts.length > 0)
